import { z } from 'zod';

import { ChatModel } from '../enums';

export const REASONING_EFFORTS = ['minimal', 'low', 'medium', 'high'] as const;

export const reasoningEffortSchema = z.enum(REASONING_EFFORTS);

export type ReasoningEffort = z.infer<typeof reasoningEffortSchema>;

export interface ModelReasoningCapability {
  /** Levels the admin may choose; every one was accepted by the provider. */
  efforts: readonly ReasoningEffort[];
  /** Level sent when the admin leaves the setting on "Default". */
  defaultEffort: ReasoningEffort;
}

const LOW_TO_HIGH = ['low', 'medium', 'high'] as const;

/**
 * Models whose reasoning effort the admin may choose. A model belongs here
 * only when every listed level was accepted by its provider route in a direct
 * API probe (2026-10-02, evals/results/preflight-2026-10/probe-effort*.mjs);
 * all other models run on the provider default and show no setting.
 *
 * - Gemini 3.5 Flash (OpenRouter): `minimal`, `low`, `medium` and `high`
 *   returned HTTP 200; `none` is rejected. `minimal` is the only level that
 *   does not reason (0 tokens vs ~800-1,250 at the others), so it is offered.
 *   The provider default reasons (~1,400 tokens), so `medium` keeps its
 *   established behaviour.
 * - Gemini 3.5 Flash-Lite and 3.8 Flash (OpenRouter): `minimal`, `low`,
 *   `medium` and `high` returned HTTP 200 (3.8 Flash `high` and its own
 *   default included); `none` is rejected. `minimal` is left out because it
 *   behaved exactly like `low` here: no reasoning tokens at either. Flash-Lite's
 *   provider default does not reason at all and failed shelf-fit arithmetic
 *   without scratch space, so it defaults to `medium`; 3.8 Flash's provider
 *   default already reasons (~800-900 tokens), so `medium` keeps its
 *   established behaviour.
 * - GPT-5.6 Luna, GPT-6 Luna, GPT-6.1 Sol (native OpenAI Responses): `low`,
 *   `medium` and `high` returned HTTP 200 and all reasoned; `minimal` was
 *   not probed and is not offered.
 */
export const MODEL_REASONING_CAPABILITIES: Readonly<
  Partial<Record<string, ModelReasoningCapability>>
> = {
  [ChatModel.Gemini35Flash]: {
    efforts: REASONING_EFFORTS,
    defaultEffort: 'medium',
  },
  [ChatModel.Gemini35FlashLite]: {
    efforts: LOW_TO_HIGH,
    defaultEffort: 'medium',
  },
  [ChatModel.Gemini38Flash]: { efforts: LOW_TO_HIGH, defaultEffort: 'medium' },
  [ChatModel.Gpt56Luna]: { efforts: LOW_TO_HIGH, defaultEffort: 'low' },
  [ChatModel.Gpt6Luna]: { efforts: LOW_TO_HIGH, defaultEffort: 'low' },
  [ChatModel.Gpt61Sol]: { efforts: LOW_TO_HIGH, defaultEffort: 'low' },
};

export function getModelReasoningCapability(
  model: string | undefined,
): ModelReasoningCapability | undefined {
  return model ? MODEL_REASONING_CAPABILITIES[model] : undefined;
}

export function modelSupportsReasoningEffort(
  model: string | undefined,
  effort: ReasoningEffort,
): boolean {
  return !!getModelReasoningCapability(model)?.efforts.includes(effort);
}

/**
 * The effort to send for `model`: the admin's choice when this model supports
 * it, otherwise the model default. Unsupported models get no effort at all,
 * so a stale value left over from a previously selected model is ignored.
 */
export function resolveReasoningEffort(
  model: string | undefined,
  configured: ReasoningEffort | null | undefined,
): ReasoningEffort | undefined {
  const capability = getModelReasoningCapability(model);

  if (!capability) {
    return undefined;
  }

  return configured && capability.efforts.includes(configured)
    ? configured
    : capability.defaultEffort;
}
